import React, { useState, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import 'react-table/react-table.css';

import {
  DEFAULT_ORDER,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT,
} from '../../../../config/constants';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const isEmptyFilterValue = (value) => {
  if (value === undefined || value === null) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return String(value).trim() === '';
};

const EnterFilterInput = ({ filter, onChange }) => {
  const [value, setValue] = useState(filter ? filter.value : '');

  return (
    <input
      type="text"
      style={{ width: '100%' }}
      value={value || ''}
      onChange={(event) => {
        const newValue = event.target.value;

        setValue(newValue);
        onChange(newValue);
      }}
    />
  );
};

EnterFilterInput.propTypes = {
  filter: PropTypes.shape({
    id: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  }),
  onChange: PropTypes.func.isRequired,
};

EnterFilterInput.defaultProps = {
  filter: null,
};

const BillingTable = ({
  columns,
  data,
  pages,
  loading,
  fetchData,
  queryProps,
}) => {
  const [page, setPage] = useState(0);

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [filtered, setFiltered] = useState([]);

  const [sorted, setSorted] = useState([]);

  const pageSizeRef = useRef(DEFAULT_PAGE_SIZE);

  const draftFilteredRef = useRef([]);

  const visibleRows =
    data.length > 0 && data.length < pageSize ? data.length : pageSize;

  const updateDraft = useCallback((columnId, value) => {
    const others = draftFilteredRef.current.filter(
      (filter) => filter.id !== columnId,
    );

    draftFilteredRef.current = isEmptyFilterValue(value)
      ? others
      : [
          ...others,
          {
            id: columnId,
            value,
          },
        ];
  }, []);

  const draftColumns = useMemo(
    () =>
      columns.map((column) => {
        if (!column.Filter || column.filterable === false) {
          return column;
        }

        const OriginalFilter = column.Filter;

        return {
          ...column,

          Filter: (filterProps) =>
            OriginalFilter({
              ...filterProps,

              onChange: (value) => updateDraft(filterProps.column.id, value),
            }),
        };
      }),
    [columns, updateDraft],
  );

  const renderEnterFilter = ({ filter, column }) => (
    <EnterFilterInput
      filter={filter}
      onChange={(value) => updateDraft(column.id, value)}
    />
  );

  const onFetchData = (state) => {
    const query = [];

    if (Array.isArray(queryProps)) {
      query.push(...queryProps);
    } else if (queryProps) {
      query.push(queryProps);
    }

    const filterQuery = (state.filtered || [])
      .filter((filter) => !isEmptyFilterValue(filter.value))
      .map((filter) => {
        const value = Array.isArray(filter.value)
          ? filter.value.join('|')
          : String(filter.value).trim();

        return `${filter.id}:${value}`;
      });

    query.push(...filterQuery);

    /*
     * Ordenamiento.
     */
    const sort =
      state.sorted && state.sorted.length > 0
        ? state.sorted.map(({ id, desc }) => `${id},${desc ? 'desc' : 'asc'}`)
        : DEFAULT_SORT;

    fetchData({
      page: state.page || 0,
      size: pageSizeRef.current,
      sort,
      order: DEFAULT_ORDER,
      query,
    });
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    const { target } = event;

    if (!(target instanceof HTMLElement)) {
      return;
    }

    if (!target.closest('.rt-thead.-filters')) {
      return;
    }

    event.preventDefault();

    setFiltered([...draftFilteredRef.current]);

    setPage(0);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div onKeyDown={handleKeyDown}>
      <ReactTable
        manual
        filterable
        columns={draftColumns}
        data={data}
        pages={pages}
        loading={loading}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        page={page}
        pageSize={pageSize}
        filtered={filtered}
        sorted={sorted}
        minRows={visibleRows}
        onPageChange={(newPage) => {
          setPage(newPage);
        }}
        onPageSizeChange={(size) => {
          pageSizeRef.current = size;

          setPageSize(size);
          setPage(0);
        }}
        onSortedChange={(newSorted) => {
          setSorted(newSorted);
          setPage(0);
        }}
        onFetchData={onFetchData}
        FilterComponent={renderEnterFilter}
        className="-striped -highlight"
      />
    </div>
  );
};

BillingTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  data: PropTypes.arrayOf(PropTypes.shape({})),

  pages: PropTypes.number,

  loading: PropTypes.bool,

  fetchData: PropTypes.func.isRequired,

  queryProps: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

BillingTable.defaultProps = {
  data: [],
  pages: undefined,
  loading: false,
  queryProps: [],
};

export default BillingTable;
